import { FormattedMessage } from 'react-intl'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { AllowedColSpan } from 'src/types/dashboard'
import ResizeHandle from '../ResizeHandle'
import { useCallback } from 'react'
import { useColumnResize } from '@hooks/useColumnResize'

import './GridItem.css'

interface GridItemProps {
  itemId: string
  width: AllowedColSpan
  onResize: (itemId: string, width: AllowedColSpan) => void
  onResizePreview: (itemId: string, width: AllowedColSpan | null) => void
  onRemove: (itemId: string) => void
  onEdit: (itemId: string) => void
  containerRef: React.RefObject<HTMLElement | null>
  children: React.ReactNode
  editable: boolean
}

export default function GridItem({
  itemId,
  width,
  onResize,
  onResizePreview,
  onRemove,
  onEdit,
  containerRef,
  children,
  editable
}: GridItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: itemId })

  const handleResizeEnd = useCallback(
    (newWidth: AllowedColSpan) => onResize(itemId, newWidth),
    [itemId, onResize]
  )

  const handleResizePreview = useCallback(
    (newWidth: AllowedColSpan | null) => onResizePreview(itemId, newWidth),
    [itemId, onResizePreview]
  )

  const {
    handlePointerDown,
    handlePointerMove,
    handlePointerUp,
    handlePointerCancel,
    isResizing,
    previewWidth
  } = useColumnResize({
    width,
    onResizeEnd: handleResizeEnd,
    onResizePreview: handleResizePreview,
    containerRef
  })

  const activeWidth = isResizing ? previewWidth : width

  const style: React.CSSProperties = {
    gridColumn: `span ${activeWidth}`,
    transform: CSS.Translate.toString(transform),
    transition: transition ?? undefined,
    opacity: isDragging ? 0.3 : 1,
    cursor: 'grab'
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="grid-item"
      {...attributes}
      {...listeners}
    >
      <div className="grid-item-actions">
        <div className="grab-handle">
          <span className="icon icon-drag" />
          <span>
            <FormattedMessage id="grab-handle.move" />
          </span>
        </div>
        {editable && (
          <button
            className="grid-item-button grid-item-edit icon icon-pen"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={() => onEdit(itemId)}
          />
        )}
        <button
          className="grid-item-button grid-item-remove icon icon-bin"
          onPointerDown={(e) => e.stopPropagation()}
          onClick={() => onRemove(itemId)}
        />
        <ResizeHandle
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerCancel}
        />
      </div>
      {children}
    </div>
  )
}
